import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { useLanguage, PriceTag } from '../context/LanguageContext';
import API from '../config/api';
import IMAGES from '../config/images';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Cart() {
  const { cartItems, removeFromCart, updateQuantity, cartTotal, clearCart } = useContext(CartContext);
  const { user, token } = useContext(AuthContext);
  const { lang, t, formatPrice } = useLanguage();
  const navigate = useNavigate();

  const [checkoutStep, setCheckoutStep] = useState('cart');
  const [checkoutForm, setCheckoutForm] = useState({
    name: '',
    email: '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
  });
  const [selectedPayment, setSelectedPayment] = useState('razorpay');
  const [subscribeNews, setSubscribeNews] = useState(true);
  const [acceptTerms, setAcceptTerms] = useState(true);
  const [createAccount, setCreateAccount] = useState(false);
  const [newAccountPassword, setNewAccountPassword] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoMessage, setPromoMessage] = useState('');
  const [addons, setAddons] = useState({
    stainResistance: false,
    rugPad: false,
  });

  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  useEffect(() => {
    if (!user) return;
    setCheckoutForm(prev => ({
      ...prev,
      name: prev.name || user.name || '',
      email: prev.email || user.email || '',
      phone: prev.phone || user.phone_number || '',
    }));
  }, [user]);

  const stainAmount = useMemo(() => (addons.stainResistance ? Math.round(cartTotal * 0.08) : 0), [addons.stainResistance, cartTotal]);
  const rugPadAmount = useMemo(() => (addons.rugPad ? Math.round(cartTotal * 0.05) : 0), [addons.rugPad, cartTotal]);
  const addonsTotal = useMemo(() => stainAmount + rugPadAmount, [stainAmount, rugPadAmount]);
  const shippingCharge = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const raw = Number(item.delivery_price ?? item.shipping_price);
      const itemDelivery = Number.isFinite(raw) ? Math.max(0, raw) : 500;
      const qty = Math.max(1, Number(item.quantity || 1));
      return sum + (itemDelivery * qty);
    }, 0);
  }, [cartItems]);
  const dispatchMeta = useMemo(() => {
    if (!cartItems.length) {
      return { min: 1, max: 3, hasMadeToOrder: false };
    }

    let minDays = Number.POSITIVE_INFINITY;
    let maxDays = 0;
    let hasMadeToOrder = false;

    for (const item of cartItems) {
      const isMadeToOrder = String(item.delivery_type || '').toLowerCase() === 'made_to_order';
      if (isMadeToOrder) hasMadeToOrder = true;

      const fallbackMin = isMadeToOrder ? 15 : 1;
      const fallbackMax = isMadeToOrder ? 20 : 3;

      const itemMinRaw = Number(item.dispatch_min_days);
      const itemMaxRaw = Number(item.dispatch_max_days);

      const itemMin = Math.max(0, Number.isFinite(itemMinRaw) ? itemMinRaw : fallbackMin);
      const itemMax = Math.max(itemMin, Number.isFinite(itemMaxRaw) ? itemMaxRaw : fallbackMax);

      minDays = Math.min(minDays, itemMin);
      maxDays = Math.max(maxDays, itemMax);
    }

    if (!Number.isFinite(minDays)) minDays = hasMadeToOrder ? 15 : 1;
    if (maxDays < minDays) maxDays = minDays;

    return { min: minDays, max: maxDays, hasMadeToOrder };
  }, [cartItems]);
  const grandTotal = useMemo(() => cartTotal + addonsTotal + shippingCharge, [cartTotal, addonsTotal, shippingCharge]);

  const setField = (key, value) => {
    setCheckoutForm(prev => ({ ...prev, [key]: value }));
  };

  const buildAddonsPayload = () => {
    const payload = [];
    if (addons.stainResistance && stainAmount > 0) {
      payload.push({ code: 'STAIN_RESISTANCE', label: 'Stain Resistance Coating', amount: stainAmount });
    }
    if (addons.rugPad && rugPadAmount > 0) {
      payload.push({ code: 'RUG_PAD', label: 'Anti-Slip Rug Pad', amount: rugPadAmount });
    }
    return payload;
  };

  const validateCheckout = () => {
    const requiredFields = ['name', 'email', 'phone', 'line1', 'city', 'state', 'postal_code'];
    for (const field of requiredFields) {
      if (!String(checkoutForm[field] || '').trim()) {
        return 'Please fill all required checkout fields.';
      }
    }
    const email = String(checkoutForm.email || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Please enter a valid email.';
    }
    if (createAccount && String(newAccountPassword).trim().length < 6) {
      return 'Please enter a password of at least 6 characters.';
    }
    return '';
  };

  const saveAddressAndContinue = () => {
    setCheckoutError('');
    const validationMessage = validateCheckout();
    if (validationMessage) {
      setCheckoutError(validationMessage);
      return;
    }
    if (!acceptTerms) {
      setCheckoutError('Please accept terms and privacy policy to continue.');
      return;
    }
    setCheckoutStep('payment');
  };

  const applyPromo = () => {
    setPromoMessage('Promo code support will be enabled in admin campaigns soon.');
  };

  const startCheckout = async () => {
    if (checkoutBusy) return;
    setCheckoutError('');

    if (checkoutStep !== 'payment') {
      setCheckoutError('Please complete address details before payment.');
      return;
    }

    if (!acceptTerms) {
      setCheckoutError('Please accept terms and privacy policy to place the order.');
      return;
    }

    if (selectedPayment !== 'razorpay') {
      setCheckoutError('Only Razorpay is active currently. Please choose Razorpay to continue.');
      return;
    }

    const validationMessage = validateCheckout();
    if (validationMessage) {
      setCheckoutError(validationMessage);
      return;
    }

    if (cartItems.length === 0) {
      setCheckoutError('Cart is empty.');
      return;
    }

    setCheckoutBusy(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Unable to load Razorpay checkout script.');
      }

      const createPayload = {
        items: cartItems.map(item => ({
          id: item.id,
          quantity: item.quantity,
          selected_size: item.selected_size || item.selectedSize || item.size || '',
          size: item.size || item.selectedSize || item.selected_size || '',
          line_price: Number(item.price || item.b2c_price || 0),
        })),
        customer: {
          name: checkoutForm.name,
          email: checkoutForm.email,
          phone: checkoutForm.phone,
        },
        shipping_address: {
          line1: checkoutForm.line1,
          line2: checkoutForm.line2,
          city: checkoutForm.city,
          state: checkoutForm.state,
          postal_code: checkoutForm.postal_code,
          country: checkoutForm.country || 'India',
        },
        addons: buildAddonsPayload(),
        create_account: !!createAccount,
        account_password: createAccount ? String(newAccountPassword || '') : '',
      };

      const createHeaders = {
        'Content-Type': 'application/json',
      };
      if (token) createHeaders.Authorization = `Bearer ${token}`;

      const createResponse = await fetch(API.orders.create, {
        method: 'POST',
        headers: createHeaders,
        body: JSON.stringify(createPayload),
      });
      const createData = await createResponse.json();

      if (!createResponse.ok || createData.status !== 'success' || !createData.data) {
        throw new Error(createData.message || 'Unable to create order.');
      }

      const checkoutData = createData.data;
      if (!checkoutData.key_id) {
        throw new Error('Razorpay key is missing in server response.');
      }

      const options = {
        key: checkoutData.key_id,
        amount: checkoutData.amount,
        currency: checkoutData.currency || 'INR',
        name: 'Kaaleen Haus',
        description: `Order ${checkoutData.order_public_id}`,
        order_id: checkoutData.razorpay_order_id,
        method: {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
          paylater: true,
        },
        prefill: {
          name: checkoutForm.name,
          email: checkoutForm.email,
          contact: checkoutForm.phone,
        },
        notes: {
          order_public_id: checkoutData.order_public_id,
        },
        theme: { color: '#8f6a00' },
        handler: async function (paymentResponse) {
          try {
            const verifyResponse = await fetch(API.orders.verify, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
              }),
            });

            const verifyData = await verifyResponse.json();
            if (!verifyResponse.ok || verifyData.status !== 'success') {
              throw new Error(verifyData.message || 'Payment verification failed.');
            }

            const confirmedOrderPublicId = verifyData.data?.order_public_id || checkoutData.order_public_id;
            sessionStorage.setItem('kh_last_order_context', JSON.stringify({
              orderPublicId: confirmedOrderPublicId,
              email: checkoutForm.email,
              phone: checkoutForm.phone,
              paymentId: paymentResponse.razorpay_payment_id,
              updatedAt: Date.now(),
            }));
            clearCart();
            navigate(`/order-success?order=${encodeURIComponent(confirmedOrderPublicId)}`);
          } catch (err) {
            setCheckoutError(err.message || 'Payment completed but verification failed.');
          } finally {
            setCheckoutBusy(false);
          }
        },
        modal: {
          ondismiss: function () {
            setCheckoutBusy(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (resp) {
        setCheckoutError(resp?.error?.description || 'Payment failed. Please try again.');
        setCheckoutBusy(false);
      });

      razorpay.open();
    } catch (err) {
      setCheckoutError(err.message || 'Unable to start checkout.');
      setCheckoutBusy(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '120px 20px' }}>
        <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '2.5rem', marginBottom: '15px' }}>{lang === 'de' ? t('cart.empty') : 'Your Cart is Empty'}</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '30px' }}>{lang === 'de' ? t('cart.emptySub') : 'Discover our handcrafted collection and find your perfect rug.'}</p>
        <Link to="/shop" className="btn-primary">{lang === 'de' ? t('cart.continueShopping') : 'Browse Collection'}</Link>
      </div>
    );
  }

  const dispatchWindowText = dispatchMeta.min === dispatchMeta.max
    ? (lang === 'de' ? `${dispatchMeta.min} Tag Versand` : `${dispatchMeta.min} day dispatch`)
    : (lang === 'de' ? `${dispatchMeta.min}-${dispatchMeta.max} Tage Versand` : `${dispatchMeta.min}-${dispatchMeta.max} day dispatch`);

  const summaryCard = (
    <aside className="kh-order-summary" style={{ background: '#fff', border: '1px solid #ddd7ca', borderRadius: '4px', padding: '16px' }}>
      <div style={{ borderBottom: '1px solid #e7e2d7', paddingBottom: '10px', marginBottom: '12px' }}>
        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, letterSpacing: '0.6px' }}>{lang === 'de' ? t('cart.orderSummary').toUpperCase() : 'ORDER SUMMARY'}</h4>
      </div>

      <div style={{ maxHeight: '160px', overflow: 'auto', marginBottom: '10px', paddingRight: '6px' }}>
        {cartItems.map((item) => {
          const price = Number(item.price || item.b2c_price || 0);
          const thumb = item.image_url || IMAGES.cartThumb;
          return (
            <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '58px 1fr auto', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ width: '58px', height: '58px', background: `url(${thumb}) center/cover`, borderRadius: '2px', border: '1px solid #ebe6da' }} />
              <div>
                <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#161616' }}>{item.name}</p>
                <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#666' }}>{lang === 'de' ? t('cart.size') : 'Size'}: {item.selected_size || item.size || '-'} | {lang === 'de' ? t('cart.quantity') : 'Qty'}: {item.quantity}</p>
              </div>
              <p style={{ margin: 0, fontSize: '12px', fontWeight: 600 }}>{formatPrice(price * item.quantity).display}</p>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'grid', gap: '6px', fontSize: '13px', color: '#282828', borderTop: '1px solid #ece6d9', paddingTop: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{lang === 'de' ? 'WARENKORBWERT' : 'CART VALUE'}</span><span>{formatPrice(cartTotal).display}</span></div>
        {addons.stainResistance && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{lang === 'de' ? 'FLECKENSCHUTZ' : 'STAIN RESISTANCE COATING'}</span><span>{formatPrice(stainAmount).display}</span></div>}
        {addons.rugPad && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{lang === 'de' ? 'ANTI-RUTSCH-UNTERLAGE' : 'RUG PAD'}</span><span>{formatPrice(rugPadAmount).display}</span></div>}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{lang === 'de' ? t('cart.shipping').toUpperCase() : 'SHIPPING'}</span><span>{shippingCharge === 0 ? (lang === 'de' ? 'KOSTENLOS' : 'FREE') : formatPrice(shippingCharge).display}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #ece6d9', paddingTop: '8px', marginTop: '4px', fontSize: '18px', fontWeight: 700 }}><span>{lang === 'de' ? t('cart.total').toUpperCase() : 'TOTAL'}</span><span>{formatPrice(grandTotal).display}</span></div>
      </div>

      <div style={{ marginTop: '12px', fontSize: '12px', color: '#3b3b3b' }}>
        <p style={{ margin: '0 0 6px' }}>Delivery charge is set per product from admin.</p>
        <p style={{ margin: '0 0 6px' }}>Estimated dispatch: {dispatchWindowText}</p>
        {dispatchMeta.hasMadeToOrder && <p style={{ margin: '0 0 6px' }}>This cart includes made-to-order items.</p>}
        <p style={{ margin: 0 }}>Inclusive of all taxes</p>
      </div>
    </aside>
  );

  return (
    <main className="kh-checkout-root" style={{ maxWidth: '1280px', margin: '0 auto', padding: '42px 26px 70px' }}>
      <section style={{ marginBottom: '20px' }}>
        <h1 className="kh-checkout-title" style={{ margin: 0, textAlign: 'center', fontFamily: 'Noto Serif, Georgia, serif', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 500, color: '#1d1d1d' }}>{checkoutStep === 'cart' ? (lang === 'de' ? t('cart.title') : 'Your Cart') : (lang === 'de' ? t('cart.checkout') : 'Checkout')}</h1>
      </section>

      {checkoutStep !== 'cart' && (
        <section className="kh-checkout-steps" style={{ maxWidth: '860px', margin: '0 auto 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', alignItems: 'center' }}>
            <div style={{ height: '3px', background: checkoutStep === 'address' || checkoutStep === 'payment' ? '#c8a96e' : '#d6d1c5' }} />
            <div style={{ height: '3px', background: checkoutStep === 'payment' ? '#c8a96e' : '#d6d1c5' }} />
            <div style={{ height: '3px', background: checkoutStep === 'payment' && !checkoutBusy ? '#c9c9c9' : '#c9c9c9' }} />
          </div>
          <div className="kh-checkout-step-labels" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', marginTop: '7px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#565656' }}>
            <span>{lang === 'de' ? 'Adresse' : 'Address'}</span>
            <span style={{ textAlign: 'center' }}>{lang === 'de' ? 'Zahlung' : 'Payment'}</span>
            <span style={{ textAlign: 'right' }}>{lang === 'de' ? 'Abgeschlossen' : 'Completed'}</span>
          </div>
        </section>
      )}

      {checkoutError && (
        <div style={{ maxWidth: '1100px', margin: '0 auto 16px', background: '#fff2f2', color: '#972323', border: '1px solid #f0b8b8', borderRadius: '4px', padding: '11px 12px', fontSize: '13px' }}>
          {checkoutError}
        </div>
      )}

      {checkoutStep === 'cart' && (
        <section className="kh-checkout-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', alignItems: 'start' }}>
          <div className="kh-panel" style={{ background: '#fff', border: '1px solid #ddd7ca', borderRadius: '4px', padding: '16px' }}>
            {cartItems.map((item) => {
              const price = Number(item.price || item.b2c_price || 0);
              const thumb = item.image_url || IMAGES.cartThumb;
              const hasTrackedStock = item?.delivery_type === 'stock' && item?.stock_quantity !== null && item?.stock_quantity !== undefined && item?.stock_quantity !== '';
              const stockLimit = hasTrackedStock ? Math.max(1, Number(item.stock_quantity)) : 5;
              const maxQtyOption = Math.max(1, Math.min(20, hasTrackedStock ? stockLimit : 5));
              return (
                <article key={item.id} className="kh-cart-item" style={{ borderBottom: '1px solid #ebe5d8', paddingBottom: '16px', marginBottom: '16px' }}>
                  <div className="kh-cart-item-row" style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto', gap: '14px' }}>
                    <div className="kh-cart-item-image" style={{ width: '100%', maxWidth: '180px', height: '210px', background: `url(${thumb}) center/cover`, border: '1px solid #ece7db' }} />
                    <div>
                      <h3 style={{ margin: 0, fontSize: '19px', fontWeight: 500, fontFamily: 'Noto Serif, Georgia, serif' }}>{item.name}</h3>
                      <p style={{ margin: '6px 0 2px', fontSize: '12px', color: '#666' }}>Hand Knotted Rug</p>
                      <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#444' }}>Size: {item.selected_size || item.size || '-'}</p>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
                        <label style={{ fontSize: '12px', color: '#4e4e4e' }}>Qty</label>
                        <select value={item.quantity} onChange={(e) => updateQuantity(item.id, Number(e.target.value))} style={{ border: '1px solid #b8b8b8', padding: '7px 10px', minWidth: '82px', background: '#fff' }}>
                          {Array.from({ length: maxQtyOption }, (_, i) => i + 1).map(q => <option key={q} value={q}>{q}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="kh-cart-price-col" style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, color: '#c56e58', fontWeight: 600, fontSize: '20px' }}>{formatPrice(price * item.quantity).display}</p>
                      <button onClick={() => removeFromCart(item.id)} style={{ marginTop: '14px', fontSize: '11px', letterSpacing: '1px', color: '#7b6d5d', textTransform: 'uppercase', textDecoration: 'underline' }}>
                        {lang === 'de' ? t('cart.remove') : 'Remove'}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}

            <div style={{ borderTop: '1px solid #ece5d6', paddingTop: '14px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '11px', letterSpacing: '1px', color: '#575757', textTransform: 'uppercase' }}>{lang === 'de' ? 'Optionale Dienstleistungen' : 'Optional Services'}</p>
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px', fontSize: '14px' }}>
                <span><input type="checkbox" checked={addons.stainResistance} onChange={(e) => setAddons(prev => ({ ...prev, stainResistance: e.target.checked }))} style={{ marginRight: '10px' }} />{lang === 'de' ? 'Fleckenschutz-Beschichtung' : 'Stain Resistance Coating'}</span>
                <strong>{formatPrice(stainAmount).display}</strong>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', fontSize: '14px' }}>
                <span><input type="checkbox" checked={addons.rugPad} onChange={(e) => setAddons(prev => ({ ...prev, rugPad: e.target.checked }))} style={{ marginRight: '10px' }} />{lang === 'de' ? 'Anti-Rutsch-Unterlage' : 'Anti-Slip Rug Pad'}</span>
                <strong>{formatPrice(rugPadAmount).display}</strong>
              </label>
            </div>
          </div>

          <div>
            {summaryCard}
            <button className="kh-cta-main" onClick={() => setCheckoutStep('address')} style={{ width: '100%', marginTop: '12px', background: '#c9725f', color: '#fff', padding: '14px', fontSize: '18px', fontWeight: 600, borderRadius: '0' }}>
              {lang === 'de' ? t('cart.checkout').toUpperCase() : 'PROCEED TO ORDER'}
            </button>
            <Link className="kh-cta-secondary" to="/shop" style={{ marginTop: '10px', display: 'block', textAlign: 'center', padding: '12px', border: '1px solid #ccc2ad', background: '#f4f4f4', color: '#292929', fontSize: '14px' }}>
              {lang === 'de' ? t('cart.continueShopping').toUpperCase() : 'BACK TO SHOPPING'}
            </Link>
            <button className="kh-cta-danger" onClick={clearCart} style={{ width: '100%', marginTop: '10px', padding: '11px', fontSize: '12px', color: '#9a2e2e', letterSpacing: '1px', textTransform: 'uppercase', border: '1px solid #ead9d9', background: '#fff' }}>
              {lang === 'de' ? 'Warenkorb leeren' : 'Clear Cart'}
            </button>
          </div>
        </section>
      )}

      {(checkoutStep === 'address' || checkoutStep === 'payment') && (
        <section className="kh-checkout-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', alignItems: 'start' }}>
          <div className="kh-panel" style={{ background: '#fff', border: '1px solid #ddd7ca', borderRadius: '4px', padding: '18px' }}>
            {checkoutStep === 'address' && (
              <>
                <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '24px', fontFamily: 'Noto Serif, Georgia, serif', fontWeight: 500 }}>{lang === 'de' ? t('cart.shippingInfo') : 'Address'}</h3>

                <p style={{ margin: '0 0 6px', fontSize: '11px', letterSpacing: '1px', color: '#575757', textTransform: 'uppercase' }}>{lang === 'de' ? 'Kontakt' : 'Contact'}</p>
                <input value={checkoutForm.email} onChange={e => setField('email', e.target.value)} placeholder={lang === 'de' ? t('cart.email') : "Email Address"} style={{ width: '100%', marginBottom: '10px', padding: '11px 12px', border: '1px solid #ddd', background: '#f8f8f8' }} />

                <p style={{ margin: '8px 0 6px', fontSize: '11px', letterSpacing: '1px', color: '#575757', textTransform: 'uppercase' }}>{lang === 'de' ? t('cart.address') : 'Shipping Address'}</p>
                <input value={checkoutForm.name} onChange={e => setField('name', e.target.value)} placeholder={lang === 'de' ? t('cart.fullName') : "Full Name *"} style={{ width: '100%', marginBottom: '10px', padding: '11px 12px', border: '1px solid #ddd', background: '#f8f8f8' }} />
                <input value={checkoutForm.line1} onChange={e => setField('line1', e.target.value)} placeholder={lang === 'de' ? t('cart.address') : "Shipping Address *"} style={{ width: '100%', marginBottom: '10px', padding: '11px 12px', border: '1px solid #ddd', background: '#f8f8f8' }} />
                <input value={checkoutForm.line2} onChange={e => setField('line2', e.target.value)} placeholder={lang === 'de' ? "Wohnung, Suite etc. (optional)" : "Apartment, suite, etc. (optional)"} style={{ width: '100%', marginBottom: '10px', padding: '11px 12px', border: '1px solid #ddd', background: '#f8f8f8' }} />

                <div className="kh-address-city-row" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 1fr', gap: '10px' }}>
                  <input value={checkoutForm.city} onChange={e => setField('city', e.target.value)} placeholder={lang === 'de' ? t('cart.city') : "City *"} style={{ width: '100%', marginBottom: '10px', padding: '11px 12px', border: '1px solid #ddd', background: '#f8f8f8' }} />
                  <input value={checkoutForm.state} onChange={e => setField('state', e.target.value)} placeholder={lang === 'de' ? t('cart.state') : "State *"} style={{ width: '100%', marginBottom: '10px', padding: '11px 12px', border: '1px solid #ddd', background: '#f8f8f8' }} />
                  <input value={checkoutForm.postal_code} onChange={e => setField('postal_code', e.target.value)} placeholder={lang === 'de' ? t('cart.pincode') : "Pincode *"} style={{ width: '100%', marginBottom: '10px', padding: '11px 12px', border: '1px solid #ddd', background: '#f8f8f8' }} />
                </div>

                <div className="kh-address-phone-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <input value={checkoutForm.phone} onChange={e => setField('phone', e.target.value)} placeholder={lang === 'de' ? t('cart.phone') : "Phone Number *"} style={{ width: '100%', marginBottom: '10px', padding: '11px 12px', border: '1px solid #ddd', background: '#f8f8f8' }} />
                  <input value={checkoutForm.country} onChange={e => setField('country', e.target.value)} placeholder={lang === 'de' ? t('cart.country') : "Country"} style={{ width: '100%', marginBottom: '10px', padding: '11px 12px', border: '1px solid #ddd', background: '#f8f8f8' }} />
                </div>

                <div style={{ display: 'grid', gap: '8px', marginTop: '6px' }}>
                  <label style={{ fontSize: '14px', color: '#303030' }}><input type="checkbox" checked={subscribeNews} onChange={e => setSubscribeNews(e.target.checked)} style={{ marginRight: '9px' }} />{lang === 'de' ? 'Abonnieren Sie unseren Newsletter für die neuesten Updates und Angebote.' : 'Subscribe to our newsletter for latest updates and offers.'}</label>
                  <label style={{ fontSize: '14px', color: '#303030' }}><input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)} style={{ marginRight: '9px' }} />{lang === 'de' ? 'Ich stimme den Nutzungsbedingungen und der Datenschutzrichtlinie zu.' : 'I agree to terms of service and privacy policy.'}</label>
                  <label style={{ fontSize: '14px', color: '#303030' }}><input type="checkbox" checked={createAccount} onChange={e => setCreateAccount(e.target.checked)} style={{ marginRight: '9px' }} />{lang === 'de' ? 'Ich möchte ein neues Konto erstellen.' : 'Want to sign up as new user.'}</label>
                </div>

                {createAccount && (
                  <input value={newAccountPassword} onChange={e => setNewAccountPassword(e.target.value)} type="password" placeholder={lang === 'de' ? "Passwort" : "Password"} style={{ width: '100%', marginTop: '10px', marginBottom: '10px', padding: '11px 12px', border: '1px solid #ddd', background: '#f8f8f8' }} />
                )}

                <div className="kh-address-actions" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button className="kh-cta-secondary" onClick={() => setCheckoutStep('cart')} style={{ flex: 1, border: '1px solid #cfc6b8', background: '#fff', padding: '13px', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase' }}>{lang === 'de' ? 'Zurück zum Warenkorb' : 'Back to Cart'}</button>
                  <button className="kh-cta-main kh-cta-main-large" onClick={saveAddressAndContinue} style={{ flex: 1.3, background: '#000', color: '#fff', padding: '13px', fontSize: '24px', fontFamily: 'Noto Serif, Georgia, serif' }}>{lang === 'de' ? 'SPEICHERN & WEITER' : 'SAVE & CONTINUE'}</button>
                </div>
              </>
            )}

            {checkoutStep === 'payment' && (
              <>
                <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '24px', fontFamily: 'Noto Serif, Georgia, serif', fontWeight: 500 }}>{lang === 'de' ? 'Zahlungsoptionen' : 'Payment Options'}</h3>

                <div style={{ border: '1px solid #e8e2d4', marginBottom: '10px', padding: '14px', background: '#fcfcfc' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: '#6f6a61' }}>{lang === 'de' ? 'Adresse' : 'Address'}</p>
                      <p style={{ margin: '6px 0 0', fontSize: '14px', color: '#222', lineHeight: 1.5 }}>
                        {checkoutForm.name}<br />
                        {checkoutForm.line1}{checkoutForm.line2 ? `, ${checkoutForm.line2}` : ''}<br />
                        {checkoutForm.city}, {checkoutForm.state} - {checkoutForm.postal_code}<br />
                        {checkoutForm.country}<br />
                        {lang === 'de' ? 'Telefon' : 'Phone'}: {checkoutForm.phone}
                      </p>
                    </div>
                    <button onClick={() => setCheckoutStep('address')} style={{ fontSize: '11px', textDecoration: 'underline', letterSpacing: '1px', textTransform: 'uppercase' }}>{lang === 'de' ? 'Bearbeiten' : 'Edit'}</button>
                  </div>
                </div>

                <div className="kh-payment-option-row" style={{ border: '1px solid #e8e2d4', marginBottom: '10px', padding: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', background: '#fff' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600, color: '#1f1f1f' }}>
                    <input type="radio" checked={selectedPayment === 'razorpay'} onChange={() => setSelectedPayment('razorpay')} />
                    {lang === 'de' ? 'Razorpay (Online bezahlen)' : 'Razorpay (Pay online)'}
                  </label>
                  <strong style={{ fontSize: '24px', fontFamily: 'Noto Serif, Georgia, serif' }}>{formatPrice(grandTotal).display}</strong>
                </div>

                <div className="kh-payment-option-row" style={{ border: '1px solid #e8e2d4', marginBottom: '10px', padding: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', background: '#f6f6f6', opacity: 0.7 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                    <input type="radio" checked={selectedPayment === 'bank_transfer'} onChange={() => setSelectedPayment('bank_transfer')} />
                    {lang === 'de' ? 'Banküberweisung (Demnächst)' : 'Bank Transfer (Coming Soon)'}
                  </label>
                  <strong>{formatPrice(grandTotal).display}</strong>
                </div>

                <div className="kh-payment-option-row" style={{ border: '1px solid #e8e2d4', marginBottom: '10px', padding: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', background: '#f6f6f6', opacity: 0.7 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                    <input type="radio" checked={selectedPayment === 'pay_online'} onChange={() => setSelectedPayment('pay_online')} />
                    {lang === 'de' ? 'Anderes Zahlungsgateway (Demnächst)' : 'Alternate Online Gateway (Coming Soon)'}
                  </label>
                  <strong>{formatPrice(grandTotal).display}</strong>
                </div>

                <button className="kh-cta-main kh-cta-main-large" onClick={startCheckout} disabled={checkoutBusy} style={{ width: '100%', background: '#000', color: '#fff', padding: '13px', fontSize: '24px', fontFamily: 'Noto Serif, Georgia, serif', opacity: checkoutBusy ? 0.65 : 1 }}>
                  {checkoutBusy ? (lang === 'de' ? 'VERARBEITUNG...' : 'PROCESSING...') : (lang === 'de' ? t('cart.proceedCheckout') : 'PLACE ORDER').toUpperCase()}
                </button>
              </>
            )}
          </div>

          <div>
            <div className="kh-panel" style={{ background: '#fff', border: '1px solid #ddd7ca', borderRadius: '4px', padding: '12px', marginBottom: '12px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '22px', fontFamily: 'Noto Serif, Georgia, serif', fontWeight: 500 }}>{lang === 'de' ? 'Aktionscode anwenden' : 'Apply A Promocode'}</h4>
              <div className="kh-promo-row" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px' }}>
                <input value={promoCode} onChange={e => setPromoCode(e.target.value)} placeholder={lang === 'de' ? 'Aktionscode' : 'Promo Code'} style={{ border: '1px solid #e2e2e2', padding: '10px', background: '#f8f8f8' }} />
                <button className="kh-cta-main" onClick={applyPromo} style={{ background: '#000', color: '#fff', padding: '10px 18px', fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase' }}>{lang === 'de' ? 'Anwenden' : 'Apply'}</button>
              </div>
              {promoMessage && <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#7e6e59' }}>{lang === 'de' ? 'Aktionscodes werden bald unterstützt.' : promoMessage}</p>}
            </div>

            {summaryCard}
          </div>
        </section>
      )}

      <style>{`
        .kh-checkout-root {
          background:
            radial-gradient(1200px 380px at 50% -220px, rgba(200,169,110,0.18), transparent 72%),
            linear-gradient(180deg, #f6f3ed 0%, #f3efe7 100%);
          border-radius: 18px;
        }
        .kh-checkout-title {
          color: #1a1a2e !important;
          letter-spacing: 0.6px;
          text-shadow: 0 1px 0 rgba(255,255,255,0.65);
        }
        .kh-panel,
        .kh-order-summary {
          border: 1px solid #e6ddcb !important;
          border-radius: 14px !important;
          background: rgba(255,255,255,0.9) !important;
          box-shadow: 0 8px 24px rgba(26, 26, 46, 0.06);
          backdrop-filter: blur(1px);
        }
        .kh-cart-item {
          border-bottom: 1px solid #eee5d7 !important;
        }
        .kh-cart-item-image {
          border-radius: 10px;
          overflow: hidden;
          border-color: #e8decb !important;
        }
        .kh-order-summary h4 {
          color: #1a1a2e;
          font-weight: 700;
          letter-spacing: 1px;
        }
        .kh-cta-main {
          background: linear-gradient(135deg, #0b1120, #17243f) !important;
          color: #f6ebd2 !important;
          border: 1px solid #0e172a !important;
          border-radius: 10px !important;
          letter-spacing: 1px;
          font-weight: 600;
          transition: transform 0.22s ease, box-shadow 0.22s ease;
        }
        .kh-cta-main:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 18px rgba(11, 17, 32, 0.24);
        }
        .kh-cta-main-large {
          font-family: var(--font-serif) !important;
          font-size: 1.15rem !important;
        }
        .kh-cta-secondary {
          background: #fff !important;
          color: #1a1a2e !important;
          border: 1px solid #d7c8a8 !important;
          border-radius: 10px !important;
          font-weight: 600;
          letter-spacing: 0.6px;
        }
        .kh-cta-danger {
          background: #fff !important;
          border: 1px solid #efcaca !important;
          border-radius: 10px !important;
          color: #9c3434 !important;
          font-weight: 600;
          letter-spacing: 0.8px;
        }
        .kh-checkout-grid input::placeholder { color: rgba(33, 33, 33, 0.45); }
        .kh-checkout-grid input,
        .kh-checkout-grid select,
        .kh-checkout-grid textarea {
          border-radius: 8px;
          border-color: #dfd4be !important;
          background: #fffcf7 !important;
        }
        @media (max-width: 980px) {
          .kh-checkout-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 820px) {
          .kh-checkout-root { padding: 28px 14px 54px !important; }
          .kh-checkout-steps { margin-bottom: 18px !important; }
          .kh-checkout-step-labels { font-size: 10px !important; letter-spacing: 0.5px !important; }
          .kh-cart-item-row { grid-template-columns: 1fr !important; gap: 12px !important; }
          .kh-cart-item-image { max-width: 100% !important; height: 180px !important; }
          .kh-cart-price-col { text-align: left !important; }
          .kh-address-city-row,
          .kh-address-phone-row,
          .kh-promo-row { grid-template-columns: 1fr !important; }
          .kh-address-actions { flex-direction: column; }
          .kh-address-actions > button { width: 100%; }
          .kh-payment-option-row { flex-direction: column; align-items: flex-start !important; }
          .kh-payment-option-row strong { font-size: 18px !important; }
          .kh-order-summary { margin-top: 8px; }
        }
        @media (max-width: 620px) {
          .kh-checkout-grid select,
          .kh-checkout-grid input,
          .kh-checkout-grid button,
          .kh-checkout-grid a { font-size: 13px !important; }
          .kh-checkout-root h1 { font-size: 1.8rem !important; }
        }
      `}</style>
    </main>
  );
}
